/**
 * NotificationBell Component
 *
 * Bell icon with unread count badge and dropdown.
 * Uses real-time Socket.io updates for instant notification delivery.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  BookOpen,
  Clock,
  AlertTriangle,
  ClipboardCheck,
  TrendingDown,
  CheckCircle,
  XCircle,
  Megaphone,
  Check,
  CheckCheck,
  ExternalLink,
  type LucideIcon,
} from 'lucide-react';
import {
  useUnreadCount,
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  type Notification,
  type NotificationType,
} from '../../hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';

// Notification type -> icon + color mapping (mirrors operator NotificationsSheet pattern)
const typeConfig: Record<
  NotificationType,
  { icon: LucideIcon; colorClass: string; bgColor: string }
> = {
  training_assigned: { icon: BookOpen, colorClass: 'text-blue-600', bgColor: 'bg-blue-100' },
  training_reminder: { icon: Clock, colorClass: 'text-amber-600', bgColor: 'bg-amber-100' },
  training_overdue: { icon: AlertTriangle, colorClass: 'text-red-600', bgColor: 'bg-red-100' },
  assessment_created: { icon: ClipboardCheck, colorClass: 'text-green-600', bgColor: 'bg-green-100' },
  assessment_score_low: { icon: TrendingDown, colorClass: 'text-red-600', bgColor: 'bg-red-100' },
  sop_processing_complete: { icon: CheckCircle, colorClass: 'text-green-600', bgColor: 'bg-green-100' },
  sop_processing_failed: { icon: XCircle, colorClass: 'text-red-600', bgColor: 'bg-red-100' },
  system_announcement: { icon: Megaphone, colorClass: 'text-purple-600', bgColor: 'bg-purple-100' },
};

function NotificationItem({
  notification,
  onMarkRead,
  onClick,
}: {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onClick: (notification: Notification) => void;
}) {
  const config = typeConfig[notification.type] || { icon: Bell, colorClass: 'text-gray-600', bgColor: 'bg-gray-100' };
  const Icon = config.icon;
  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });

  return (
    <div
      className={`flex gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
        !notification.isRead ? 'bg-blue-50/50' : ''
      }`}
      onClick={() => onClick(notification)}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center`}>
        <Icon size={20} strokeWidth={1.5} className={config.colorClass} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${!notification.isRead ? 'font-semibold' : 'font-medium'} text-gray-900`}>
          {notification.title}
        </p>
        <p className="text-sm text-gray-600 line-clamp-2">{notification.message}</p>
        <p className="text-xs text-gray-400 mt-1">{timeAgo}</p>
      </div>

      {/* Unread indicator / Mark as read button */}
      {!notification.isRead && (
        <button
          className="flex-shrink-0 w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead(notification.id);
          }}
          title="Mark as read"
        >
          <Check className="w-4 h-4 text-gray-500" />
        </button>
      )}
    </div>
  );
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch data
  const { data: unreadData } = useUnreadCount();
  const { data: notificationsData, isLoading } = useNotifications({ limit: 10 });
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const unreadCount = unreadData?.count ?? 0;
  const notifications = notificationsData?.data ?? [];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }

    // Navigate based on notification type
    const data = notification.data as Record<string, unknown> | null;
    switch (notification.type) {
      case 'training_assigned':
      case 'training_reminder':
      case 'training_overdue':
        if (data?.materialId) {
          navigate(`/learning?materialId=${data.materialId}`);
        } else {
          navigate('/learning');
        }
        break;
      case 'assessment_created':
      case 'assessment_score_low':
        if (data?.assessmentId) {
          navigate(`/assessments/${data.assessmentId}`);
        } else {
          navigate('/assessments');
        }
        break;
      case 'sop_processing_complete':
      case 'sop_processing_failed':
        if (data?.sopId) {
          navigate(`/sops/${data.sopId}`);
        } else {
          navigate('/sops');
        }
        break;
      default:
        // Stay on current page
        break;
    }

    setIsOpen(false);
  };

  const handleMarkAllRead = () => {
    markAllAsRead.mutate();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-medium flex items-center justify-center transform translate-x-1/4 -translate-y-1/4">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                onClick={handleMarkAllRead}
                disabled={markAllAsRead.isPending}
              >
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <Bell className="w-8 h-8 mb-2 text-gray-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={(id) => markAsRead.mutate(id)}
                    onClick={handleNotificationClick}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-200 bg-gray-50">
              <button
                className="w-full px-4 py-3 text-sm text-emerald-600 hover:text-emerald-700 hover:bg-gray-100 flex items-center justify-center gap-1 transition-colors"
                onClick={() => {
                  navigate('/settings/notifications');
                  setIsOpen(false);
                }}
              >
                View all notifications
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
